import type { AuditEntry } from '@jmc/shared';
import { formatCount } from '@jmc/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import {
  cx,
  EmptyState,
  ErrorState,
  Field,
  PageHeader,
  Panel,
  Select,
  TableSkeleton,
} from '../components/ui.js';
import { AuditTable } from '../components/AuditTrail.js';

interface ActivityRow {
  name: string;
  designation: string | null;
  issued: number;
  returned: number;
  outstanding: number;
  overdue: number;
}

export function ReportsPage() {
  const { t } = useI18n();
  const [days, setDays] = useState(90);

  const query = useQuery({
    queryKey: ['activity', days],
    queryFn: () => api<{ days: number; rows: ActivityRow[] }>(`/reports/activity?days=${days}`),
  });

  return (
    <>
      <PageHeader
        title={t('reports.activity')}
        actions={
          <Select
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            className="w-auto"
            aria-label={t('reports.days', { n: days })}
          >
            {[30, 90, 180, 365].map((option) => (
              <option key={option} value={option}>
                {t('reports.days', { n: option })}
              </option>
            ))}
          </Select>
        }
      />

      <Panel bodyClassName="overflow-x-auto">
        {query.isLoading && <TableSkeleton columns={5} />}
        {query.isError && (
          <div className="p-4">
            <ErrorState error={query.error} onRetry={() => void query.refetch()} />
          </div>
        )}
        {query.data?.rows.length === 0 && <EmptyState title={t('common.none')} />}
        {query.data && query.data.rows.length > 0 && (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('users.name')}</th>
                <th className="text-right">{t('reports.issued')}</th>
                <th className="text-right">{t('reports.returned')}</th>
                <th className="text-right">{t('reports.outstanding')}</th>
                <th className="text-right">{t('status.overdue')}</th>
              </tr>
            </thead>
            <tbody>
              {query.data.rows.map((row) => (
                <tr key={row.name}>
                  <td>
                    <div className="font-medium">{row.name}</div>
                    {row.designation && (
                      <div className="text-[0.8125rem] text-ink-500">
                        {row.designation}
                      </div>
                    )}
                  </td>
                  <td className="text-right tabular">{formatCount(row.issued)}</td>
                  <td className="text-right tabular">{formatCount(row.returned)}</td>
                  <td className="text-right tabular">{formatCount(row.outstanding)}</td>
                  <td
                    className={cx(
                      'text-right tabular',
                      row.overdue > 0 && 'font-medium text-amber-ink',
                    )}
                  >
                    {row.overdue > 0 ? formatCount(row.overdue) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </>
  );
}

export function AuditPage() {
  const { t } = useI18n();
  // Without a filter the trail is one undivided stream, so "what has been
  // done to the files this week" meant scrolling past every sign-in.
  const [entity, setEntity] = useState('');

  const query = useQuery({
    queryKey: ['audit', entity],
    queryFn: () =>
      api<{ entries: AuditEntry[] }>(
        `/reports/audit?limit=200${entity ? `&entity=${encodeURIComponent(entity)}` : ''}`,
      ),
  });

  return (
    <>
      <PageHeader
        title={t('audit.title')}
        subtitle="Append-only. Entries cannot be edited or deleted by the application."
      />

      <Panel className="mb-4" bodyClassName="grid gap-3 px-4 py-4 sm:grid-cols-3">
        <Field label={t('audit.entity')}>
          <Select value={entity} onChange={(event) => setEntity(event.target.value)}>
            <option value="">{t('common.all')}</option>
            <option value="file_record">{t('audit.entity.file_record')}</option>
            <option value="storage_bag">{t('audit.entity.storage_bag')}</option>
            <option value="file_movement">{t('audit.entity.file_movement')}</option>
            <option value="app_user">{t('audit.entity.app_user')}</option>
            <option value="import_batch">{t('audit.entity.import_batch')}</option>
          </Select>
        </Field>
      </Panel>

      <Panel bodyClassName="overflow-x-auto">
        {query.isError ? (
          <div className="p-4">
            <ErrorState error={query.error} onRetry={() => void query.refetch()} />
          </div>
        ) : (
          <AuditTable entries={query.data?.entries ?? []} loading={query.isLoading} />
        )}
      </Panel>
    </>
  );
}
